import { useCallback, useEffect, useRef, useState } from 'react'
import { ChartDatePicker } from './chart-date-picker'
import { useLayoutPanel } from '@/workspace/use-layout-panel'
import { usePanelMode } from '@/lib/panel-mode'
import { useReplayPrefs } from '@/features/charts/replay-prefs-store'
import { getExchangeToday } from '@/lib/exchange-time'
import { resolveReplayStart } from '@/features/charts/replay-time'
import type { PanelState } from '@/workspace/types'
import type { Period } from '@edge/charts'

interface PanelStateWithFocusDate extends PanelState {
  focusDate?: string | null
  /** New York HH:MM of a scanner minute snapshot (scanner panels, flag on). */
  focusTime?: string | null
  /** Ephemeral day a shift+click drill-down is anchored to (see chart types). */
  drillDate?: string | null
  period?: Period
}

/**
 * Intraday timeframes are the only ones where a start TIME can mean anything:
 * a 1D bucket swallows the whole session. No period on the panel yet means the
 * chart's own default, which is daily.
 */
function replayTimesApply(period: Period | undefined): boolean {
  // Type-only import of Period on purpose: pulling the runtime `@edge/charts`
  // barrel in here drags klinecharts' indicator registration into every host
  // that renders a panel header.
  return period?.timespan === 'minute' || period?.timespan === 'hour'
}

/**
 * ChartDatePicker wired to a panel-local focusDate (chart + scanner headers).
 * Writes always go panel-local; `groupFocusDate` is the broadcast value used
 * only as a display fallback when no local override is set.
 *
 * While a drill-down is open (`drillDate` set), the picker shows and edits the
 * DRILL's day instead, so the header reflects the drilled day (yellow) and
 * lets the user re-anchor it, without touching the underlying chart's date.
 *
 * Chart panels additionally get Replay Mode folded in (`replayEnabled`): the
 * calendar becomes the single place a replay's date AND time are chosen, and
 * the toggle doubles as the session's on/off switch.
 *
 * Spec: docs/superpowers/specs/2026-08-27-chart-replay-calendar-design.md
 */
export function ChartLocalDatePicker({
  panelId,
  groupFocusDate,
  replayEnabled = false,
  minuteSnapshot = false,
  onAction,
}: {
  panelId: string
  groupFocusDate?: string | null
  /** Chart panels that can host a replay (see PanelHeader's `canBarReplay`). */
  replayEnabled?: boolean
  /**
   * Scanner header with the minute-snapshot flag on: the calendar gains a
   * New York time field (panel-local `focusTime`) instead of replay.
   */
  minuteSnapshot?: boolean
  /** Registered panel-action dispatch: how the header reaches the chart body. */
  onAction?: (actionId: string) => void
}) {
  const { panelState, updatePanel } = useLayoutPanel<PanelStateWithFocusDate>(panelId)
  const drilling = panelState?.drillDate != null
  const focusDate = drilling
    ? (panelState?.drillDate ?? null)
    : (panelState?.focusDate ?? groupFocusDate ?? null)
  const onFocusDate = useCallback(
    (date: string | null) => {
      if (drilling) return updatePanel({ drillDate: date })
      // A minute only exists on a completed session: going back to today (or
      // live) drops the time, while hopping between past days keeps it so the
      // same minute can be compared across days.
      const pastDay = !!date && date < getExchangeToday()
      updatePanel(
        minuteSnapshot && !pastDay ? { focusDate: date, focusTime: null } : { focusDate: date },
      )
    },
    [updatePanel, drilling, minuteSnapshot],
  )
  const focusTime = panelState?.focusTime ?? null
  const onFocusTime = useCallback(
    (time: string | null) => updatePanel({ focusTime: time }),
    [updatePanel],
  )

  // ─── Replay ───
  const replayRunning = usePanelMode((s) => !!s.replayOpen[panelId])
  const request = usePanelMode((s) => s.replayRequest[panelId])
  const clearReplayRequest = usePanelMode((s) => s.clearReplayRequest)
  const replayTime = useReplayPrefs((s) => s.replayTime)
  const setReplayTime = useReplayPrefs((s) => s.setReplayTime)

  const [open, setOpen] = useState(false)
  // "Armed" = the two-step form is engaged but no session is running yet. A
  // RUNNING session also reads as engaged, which is what lets the same toggle
  // switch a replay back off.
  const [armed, setArmed] = useState(false)
  const [stagedDate, setStagedDate] = useState<string | null>(null)
  // Which cell the pending replay targets (right-click inside a grid).
  const cellIdRef = useRef<string | null>(null)

  // A body-side entry point (⋯ menu, right-click) raises the picker here.
  const nonce = request?.nonce
  useEffect(() => {
    if (nonce == null || !request) return
    cellIdRef.current = request.cellId
    setStagedDate(request.date)
    setArmed(true)
    setOpen(true)
    clearReplayRequest(panelId)
    // `request` is consumed by nonce; re-running on identity churn would reopen
    // a picker the user just dismissed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce])

  // Closing the calendar disarms, so the NEXT visit is the ordinary quick-click
  // date picker again. Without this, a user who armed once would find every
  // later day-click silently staging a replay instead of applying a date.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (next) return
      setStagedDate(null)
      if (!replayRunning) setArmed(false)
    },
    [replayRunning],
  )

  // A session ending elsewhere (the strip's ✕) also clears the armed flag:
  // replay is off, so the calendar goes back to hot apply.
  const wasRunningRef = useRef(replayRunning)
  useEffect(() => {
    if (wasRunningRef.current && !replayRunning) setArmed(false)
    wasRunningRef.current = replayRunning
  }, [replayRunning])

  const handleReplayActiveChange = useCallback(
    (next: boolean) => {
      setArmed(next)
      // Switching OFF while a session runs is an exit: the chart goes back to
      // whatever day the user was on before replay started.
      if (!next && replayRunning) {
        onAction?.('bar-replay:exit')
        setOpen(false)
      }
    },
    [replayRunning, onAction],
  )

  const handleReplayStart = useCallback(
    (dateStr: string, selection: typeof replayTime) => {
      const ts = resolveReplayStart(dateStr, selection)
      if (ts == null) return
      const cellId = cellIdRef.current
      cellIdRef.current = null
      onAction?.(`bar-replay:start:${ts}${cellId ? `:${cellId}` : ''}`)
    },
    [onAction],
  )

  return (
    <ChartDatePicker
      focusDate={focusDate}
      onFocusDate={onFocusDate}
      open={open}
      onOpenChange={handleOpenChange}
      scanTime={
        minuteSnapshot && !replayEnabled
          ? {
              time: focusDate && focusDate < getExchangeToday() ? focusTime : null,
              onTimeChange: onFocusTime,
            }
          : undefined
      }
      replay={
        replayEnabled
          ? {
              active: armed || replayRunning,
              onActiveChange: handleReplayActiveChange,
              onStart: handleReplayStart,
              time: replayTime,
              onTimeChange: setReplayTime,
              timesEnabled: replayTimesApply(panelState?.period),
              stagedDate,
            }
          : undefined
      }
    />
  )
}
